// Canvas utilities.
//
// NOTE: nothing in the app calls the filters below any more. binarizeImage,
// increaseContrastImage, denoiseAndSharpenImage and upscaleImage were the
// pre-upload OCR preprocessing removed in DYS-013, on measurements: CER got worse
// on every page that had ground truth, and on a book page photographed with a
// shadow across it the threshold erased two thirds of the text. Server-side
// `sharp` does the only resizing now.
//
// The file is kept because scripts/ocr-bench/apply-client-pipeline.mjs loads it
// verbatim to reproduce the old behaviour - re-running that A/B against a future
// model should not mean rewriting the algorithm from memory. loadImageToCanvas is
// still used by picture-screen.tsx for rotation.

/**
 * Load image into a canvas
 */
export async function loadImageToCanvas(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0);
            resolve({ canvas, ctx, img });
        };
        img.onerror = reject;
        img.src = src;
    });
}

/**
 * Upscale image if resolution is low
 */
export function upscaleImage(canvas, factor = 2) {
    const upCanvas = document.createElement("canvas");
    upCanvas.width = canvas.width * factor;
    upCanvas.height = canvas.height * factor;
    const ctx = upCanvas.getContext("2d");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(canvas, 0, 0, upCanvas.width, upCanvas.height);
    return upCanvas;
}

/**
 * Binarize image (convert to black & white using thresholding)
 */
export function binarizeImage(canvas, threshold = 128) {
    const ctx = canvas.getContext("2d");
    const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imgData.data;

    for (let i = 0; i < data.length; i += 4) {
        const avg = (data[i] + data[i + 1] + data[i + 2]) / 3;
        const val = avg < threshold ? 0 : 255;
        data[i] = data[i + 1] = data[i + 2] = val;
    }

    ctx.putImageData(imgData, 0, 0);
    return canvas;
}

/**
 * Increase contrast
 */
export function increaseContrastImage(canvas, contrast = 40) {
    // contrast: [-255, 255]
    const ctx = canvas.getContext("2d");
    const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imgData.data;

    const factor = (259 * (contrast + 255)) / (255 * (259 - contrast));

    for (let i = 0; i < data.length; i += 4) {
        data[i] = factor * (data[i] - 128) + 128;
        data[i + 1] = factor * (data[i + 1] - 128) + 128;
        data[i + 2] = factor * (data[i + 2] - 128) + 128;
    }

    ctx.putImageData(imgData, 0, 0);
    return canvas;
}

/**
 * Simple denoise + sharpen filter using convolution
 */
export function denoiseAndSharpenImage(canvas) {
    const ctx = canvas.getContext("2d");
    const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imgData.data;

    // Sharpen kernel
    const kernel = [
        0, -1, 0,
        -1, 5, -1,
        0, -1, 0
    ];

    const side = Math.round(Math.sqrt(kernel.length));
    const half = Math.floor(side / 2);

    const copy = new Uint8ClampedArray(data);

    for (let y = 0; y < canvas.height; y++) {
        for (let x = 0; x < canvas.width; x++) {
            let r = 0, g = 0, b = 0;
            for (let ky = 0; ky < side; ky++) {
                for (let kx = 0; kx < side; kx++) {
                    const px = x + kx - half;
                    const py = y + ky - half;
                    if (px >= 0 && px < canvas.width && py >= 0 && py < canvas.height) {
                        const idx = (py * canvas.width + px) * 4;
                        const wt = kernel[ky * side + kx];
                        r += copy[idx] * wt;
                        g += copy[idx + 1] * wt;
                        b += copy[idx + 2] * wt;
                    }
                }
            }
            const idx = (y * canvas.width + x) * 4;
            data[idx] = Math.min(Math.max(r, 0), 255);
            data[idx + 1] = Math.min(Math.max(g, 0), 255);
            data[idx + 2] = Math.min(Math.max(b, 0), 255);
        }
    }

    ctx.putImageData(imgData, 0, 0);
    return canvas;
}

/**
 * Crop around text (requires bounding box, here just an example)
 * @param {*} canvas
 * @param {*} x
 * @param {*} y
 * @param {*} width
 * @param {*} height
 */
export function cropAroundTextImage(canvas, x, y, width, height) {
    const croppedCanvas = document.createElement("canvas");
    croppedCanvas.width = width;
    croppedCanvas.height = height;
    const ctx = croppedCanvas.getContext("2d");
    ctx.drawImage(canvas, x, y, width, height, 0, 0, width, height);
    return croppedCanvas;
}

/**
 * Downscale so the long edge is at most `maxEdge`, preserving aspect ratio.
 *
 * Not preprocessing, and deliberately not in the same family as the filters above.
 * It applies no threshold, no contrast curve and no sharpening - it only removes
 * pixels the server is going to remove anyway: `/api/ocr` resizes to 2000x2000
 * with `fit: inside, withoutEnlargement`, so anything larger is bytes uploaded to
 * be discarded.
 *
 * It exists because the OCR preprocessing removed in DYS-013 was quietly doing two
 * jobs. Downscaling by 50% destroyed the page (measured), and it also kept the
 * upload under MAX_UPLOAD_BYTES. Deleting it outright fixed the first and broke
 * the second: two of the five test photographs are over 4 MB as taken, including
 * the one that scores CER 0.002.
 *
 * Returns the canvas unchanged when it already fits, so a small image is never
 * enlarged and never re-encoded.
 */
export function fitWithinMaxEdge(canvas, maxEdge) {
    const longEdge = Math.max(canvas.width, canvas.height);
    if (longEdge <= maxEdge) return canvas;

    const scale = maxEdge / longEdge;
    const fitted = document.createElement("canvas");
    fitted.width = Math.round(canvas.width * scale);
    fitted.height = Math.round(canvas.height * scale);
    const ctx = fitted.getContext("2d");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(canvas, 0, 0, fitted.width, fitted.height);
    return fitted;
}
